import React from 'react';
import { AgentMessage } from '../../hooks/useAgentConversation';
import EquipmentCard from './EquipmentCard';
import PartCard from './PartCard';
import ServiceCustomerCard from './ServiceCustomerCard';
import ServiceMachineCard from './ServiceMachineCard';
import ServicePackageCard from './ServicePackageCard';
import CustomerInfoForm, { CustomerInfoFormValues } from './CustomerInfoForm';
import VerificationCodePrompt from './VerificationCodePrompt';

interface ChatMessageProps {
  message: AgentMessage;
  orgName: string;
  // True once the conversation has ended — renders every interactive card inert.
  disabled?: boolean;
  onAskAboutEquipment?: (title: string, stockNumber: string) => void;
  onAddToCart?: (partNumber: string) => void;
  onSendMessage?: (text: string) => void;
  onSubmitCustomerInfoForm?: (values: CustomerInfoFormValues) => void;
  onSubmitVerificationCode?: (code: string) => void;
  onSkipVerification?: () => void;
  onResendVerificationCode?: () => void;
}

function formatMessage(content: string): React.ReactNode[] {
  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i}>{bold[1]}</strong>;
    return part;
  });
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  orgName,
  disabled = false,
  onAskAboutEquipment,
  onAddToCart,
  onSendMessage,
  onSubmitCustomerInfoForm,
  onSubmitVerificationCode,
  onSkipVerification,
  onResendVerificationCode,
}) => {
  const hasCards = message.actionCards && message.actionCards.length > 0;

  return (
    <div
      className={`agent-chat__message agent-chat__message--${message.role}${
        hasCards ? ' agent-chat__message--has-cards' : ''
      }`}
    >
      <div className="agent-chat__message-bubble">{formatMessage(message.content)}</div>
      {hasCards && (
        <div className="agent-chat__equipment-cards">
          {message.actionCards!.map((card, index) => {
            if (card.type === 'part') {
              return (
                <PartCard
                  key={card.data.part_number || index}
                  card={card}
                  onAddToCart={onAddToCart}
                />
              );
            }
            if (card.type === 'customer_info_form') {
              return (
                <CustomerInfoForm
                  key={`customer-info-form-${index}`}
                  orgName={orgName}
                  prefill={card.data?.prefill}
                  disabled={disabled}
                  onSubmit={(values) => onSubmitCustomerInfoForm?.(values)}
                />
              );
            }
            if (card.type === 'verification_code_prompt') {
              return (
                <VerificationCodePrompt
                  key={`verify-prompt-${index}`}
                  channel={card.data.channel}
                  maskedDestination={card.data.masked_destination}
                  canResend={card.data.can_resend ?? true}
                  error={card.data.error ?? null}
                  disabled={disabled}
                  onSubmit={(code) => onSubmitVerificationCode?.(code)}
                  onSkip={() => onSkipVerification?.()}
                  onResend={() => onResendVerificationCode?.()}
                />
              );
            }
            if (card.type === 'customer_confirm') {
              return (
                <ServiceCustomerCard
                  key={`customer-${index}`}
                  data={card.data}
                  disabled={disabled}
                  onConfirm={({ smsConsent }) =>
                    onSendMessage?.(
                      smsConsent
                        ? 'Confirmed — that information is correct. Text updates: yes. Please continue.'
                        : 'Confirmed — that information is correct. Text updates: no. Please continue.',
                    )
                  }
                  onEdit={() => onSendMessage?.('I need to edit my information.')}
                />
              );
            }
            if (card.type === 'machine_selection') {
              return (
                <ServiceMachineCard
                  key={`machines-${index}`}
                  machines={card.data.machines}
                  disabled={disabled}
                  onSelect={(machine) =>
                    onSendMessage?.(
                      `I'd like service on my ${machine.make_name} ${machine.machine_name}` +
                      `${machine.pfw_serial_number ? ` (S/N ${machine.pfw_serial_number})` : ''}.`
                    )
                  }
                />
              );
            }
            if (card.type === 'package_selection') {
              return (
                <ServicePackageCard
                  key={`packages-${index}`}
                  packages={card.data.packages}
                  disabled={disabled}
                  onSelect={(pkg) =>
                    onSendMessage?.(`I'd like the ${pkg.name} package.`)
                  }
                />
              );
            }
            if (card.type === 'equipment_confirm') {
              const { make_name, machine_name } = card.data;
              return (
                <div key={`equipment-${index}`} className="service-request-card service-request-card--equipment">
                  <div className="service-request-card__header">
                    <h3 className="service-request-card__title">Confirm Equipment</h3>
                  </div>
                  <div className="service-request-card__content">
                    <div className="service-request-card__value">
                      {make_name} {machine_name}
                    </div>
                  </div>
                  <div className="service-request-card__actions">
                    <button
                      type="button"
                      className="service-request-card__btn service-request-card__btn--primary"
                      onClick={() => onSendMessage?.('Yes, that equipment is correct.')}
                      disabled={disabled}
                    >
                      Yes, that's it
                    </button>
                    <button
                      type="button"
                      className="service-request-card__btn service-request-card__btn--secondary"
                      onClick={() => onSendMessage?.('No, that\'s not my equipment.')}
                      disabled={disabled}
                    >
                      No, different equipment
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <EquipmentCard
                key={card.data?.stock_number || index}
                card={card as any}
                onAskAbout={(title, stockNumber) =>
                  onAskAboutEquipment?.(title, stockNumber)
                }
              />
            );
          })}
        </div>
      )}
      <div className="agent-chat__message-time">
        {message.timestamp.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </div>
    </div>
  );
};

export default ChatMessage;
